import { useCallback, useMemo, useRef } from 'react';

import { datadogRum } from '@datadog/browser-rum';

import ReferralForm from 'components/modals/referral/form/ReferralForm';
import { referralFormValidation } from 'components/modals/referral/form/referralFormValidation';
import { ActivityInputValues } from 'components/shared/activityInput/useActivityInput';
import StandardModal, { PatientName } from 'components/shared/StandardModal';
import { FormProvider } from 'context/form';
import { FormHelpers, FormMeta, FormValues } from 'context/form/types';
import Upload from 'hooks/useDirectUpload/Upload';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { clientHasIntegration } from 'lib/clientIntegration';
import { simpleDashDate } from 'lib/date';
import { extractDigits } from 'lib/phone';
import Episode from 'models/Episode';
import Group from 'models/Group';
import GroupType from 'models/GroupType';
import LocationEpisode from 'models/LocationEpisode';
import { ReviewType } from 'models/Review';
import { createReview } from 'services/api/reviews';
import { useToastActions } from 'stores/toastStore';

import { buildReferralFormSteps, ReferralSteps, USState } from './form/helpers';

export type SelectValue = { label: string; value: string };
export const emptyValue: SelectValue = { label: '', value: '' };

export type ReferToFormValues = {
  note: ActivityInputValues;
  providerType: GroupType;
  group: Group;
  // The pick is a provider that uses central intake, referred to as a whole: `group` holds the client.
  referToClient?: boolean;
  previousReviewId?: string;
  removedAttachmentIds?: string[];
  includeCcd?: boolean;
};

export type ReferralDeterminantsFormValues = {
  expectedDischargeDate?: Date;
  address1?: string;
  address2?: string;
  city?: string;
  state?: USState;
  zip?: string;
  phone?: string;
};

export type ReferralFormValues = Partial<ReferToFormValues & ReferralDeterminantsFormValues>;

export type ReferralFormMeta = {
  currentStep: ReferralSteps;
  isSubmitting: boolean;
  isUploadingAttachments: boolean;
  /** Number of uploads currently shown as chips in the ReferTo step's
   * attachment list. Tracked in meta (separate from form values) so
   * handleSubmit can verify the payload matches what the user sees —
   * form values silently lost uploads once before (OLIO-2638). */
  attachedUploadCount?: number;
  submitText: string;
  steps: ReferralSteps[];
  onCancel: () => void;
  onPreviousStep: (setMeta: (key: string, value: any) => void, currentStep: ReferralSteps) => void;
  onNextStep: (setMeta: (key: string, value: any) => void, currentStep: ReferralSteps) => void;
};

type SendReferralModalProps = {
  onCancel: () => void;
  onSuccess: () => void;
  locationEpisode: LocationEpisode;
  patientName: string;
  episode: Episode;
};

export default function SendReferralModal(props: SendReferralModalProps) {
  const { onCancel, onSuccess, patientName, locationEpisode, episode } = props;
  const { addToast } = useToastActions();
  const isSubmittingRef = useRef(false);

  const invalidateQueries = useInvalidateQueries();

  const { mutate } = useMutateWithToast({
    mutationFn: createReview,
    onSuccess: () => {
      invalidateQueries({
        activities: true,
        locationEpisode: { id: locationEpisode.id },
        referrals: true,
        attachmentsCount: true,
      });
      onSuccess();
    },
  });

  const latestReferralData = locationEpisode.latestReferralReview?.data;
  const projectedDischargeDate = locationEpisode.projectedDischargeReview?.date;
  const importedPatientValues = useMemo(
    () => ({ address: episode.patient.address, phoneNumber: episode.patient.phoneNumber }),
    [episode.patient.address, episode.patient.phoneNumber]
  );

  const canAutoIncludeCcd =
    clientHasIntegration(locationEpisode.owner.client) && locationEpisode.patient.hasIntegrationExternalId;

  // Mirror the re-send predicate used in ReferToStep — when the previous
  // referral was rejected/canceled, its CCD carries over via the existing
  // attachment flow, so we don't want a fresh ccd.generate event. Starting
  // includeCcd at false (instead of true) prevents the form from
  // submitting a stale truthy default that the user can't toggle off
  // (the checkbox is also hidden in this case).
  const latestReferral = locationEpisode.latestReferralReview;
  const shouldCarryOverAttachments = !!latestReferral && (latestReferral.rejected || latestReferral.canceled);

  const initialValues = useMemo(() => {
    return {
      expectedDischargeDate: projectedDischargeDate ?? undefined,
      address1: latestReferralData?.address1 ?? importedPatientValues?.address?.address1,
      address2: latestReferralData?.address2 ?? importedPatientValues?.address?.address2,
      city: latestReferralData?.city ?? importedPatientValues?.address?.city,
      state: latestReferralData?.state ?? importedPatientValues?.address?.state,
      zip: (latestReferralData?.zip ?? importedPatientValues?.address?.zip)?.toString().slice(0, 5) ?? '',
      phone: '',
      includeCcd: canAutoIncludeCcd && !shouldCarryOverAttachments,
    };
  }, [
    latestReferralData,
    importedPatientValues,
    projectedDischargeDate,
    canAutoIncludeCcd,
    shouldCarryOverAttachments,
  ]);

  const handleSubmit = async (
    values: FormValues<ReferralFormValues>,
    meta: FormMeta<ReferralFormMeta>,
    helpers: FormHelpers<ReferralFormValues, ReferralFormMeta>
  ) => {
    if (isSubmittingRef.current) return;

    // Defense-in-depth: never submit while an attachment upload is still in
    // flight. The step buttons are disabled in this state, but guard the
    // handler too so a programmatic submit (or a brief state-settle window)
    // can't send a payload missing its not-yet-completed attachments
    // (OLIO-2585).
    if (meta.isUploadingAttachments) {
      addToast({ text: 'Please wait for attachments to finish uploading' });
      return;
    }

    // Filter out carried-over attachments from note - API will handle copying them
    const newUploads = values.note?.attachments?.filter((item): item is Upload => item instanceof Upload) ?? [];

    // Tripwire (OLIO-2638): the payload is built from values.note, but the
    // chips the user sees render from ActivityInput's own uploads state. Form
    // state once lost its uploads between attach and submit while the chips
    // stayed visible, and the referral was silently created without its
    // documents. If the two ever disagree again, block the send and report it
    // instead of losing documents.
    const attachedUploadCount = meta.attachedUploadCount ?? 0;
    if (newUploads.length !== attachedUploadCount) {
      datadogRum.addError(
        new Error(
          `Referral submit blocked: ${attachedUploadCount} upload(s) attached in the UI but ` +
            `${newUploads.length} present in form state (OLIO-2638)`
        )
      );
      addToast({
        text: 'Something went wrong with your attachments. Please remove and re-attach them, then try again',
      });
      return;
    }

    isSubmittingRef.current = true;

    helpers.setMeta('isSubmitting', true);

    delete values.note?.escalationType;

    mutate(
      {
        locationEpisodeId: locationEpisode.id,
        ...(values.referToClient ? { referredClientId: values.group?.id } : { groupId: values.group?.id ?? '' }),
        type: ReviewType.REFERRAL,
        note: values.note
          ? {
              ...values.note,
              attachments: newUploads.map((upload) => upload.serialize()),
            }
          : undefined,
        previousReviewId: values.previousReviewId,
        removedAttachmentIds: values.removedAttachmentIds,
        expectedDischargeDate: simpleDashDate(values.expectedDischargeDate),
        address1: values.address1,
        address2: values.address2,
        city: values.city,
        state: values.state,
        zip: values.zip,
        phoneNumber: extractDigits(values.phone || '') || undefined,
        includeCcd: canAutoIncludeCcd ? !!values.includeCcd : undefined,
      },
      {
        onError: () => {
          addToast({ text: 'Something went wrong. Please try again' });
          isSubmittingRef.current = false;
        },
        onSettled: () => {
          helpers.setMeta('isSubmitting', false);
          isSubmittingRef.current = false;
        },
      }
    );
  };

  const steps = useMemo(() => buildReferralFormSteps(), []);

  // Memoized so the provider isn't handed a new schema instance on every
  // re-render. FormProvider also guards against this internally (schema held
  // in a ref), but there's no reason to rebuild the schema each render
  // (OLIO-2638).
  const yupSchema = useMemo(() => referralFormValidation(), []);

  const onPreviousStep = useCallback(
    (setMeta: (key: string, value: any) => void, currentStep: ReferralSteps) => {
      if (currentStep === ReferralSteps.ReferTo) {
        onCancel();
        return;
      }

      const currentIndex = steps.indexOf(currentStep);
      setMeta('currentStep', steps[currentIndex - 1]);
    },
    [steps, onCancel]
  );

  const onNextStep = useCallback(
    (setMeta: (key: string, value: any) => void, currentStep: ReferralSteps) => {
      const currentIndex = steps.indexOf(currentStep);

      setMeta('currentStep', steps[currentIndex + 1]);
    },
    [steps]
  );

  return (
    <StandardModal title='Refer Patient' onCancel={onCancel}>
      <PatientName>Patient: {patientName}</PatientName>
      <FormProvider<ReferralFormValues, ReferralFormMeta>
        onSubmit={handleSubmit}
        yupSchema={yupSchema}
        initialValues={initialValues as any}
        initialMeta={{
          currentStep: ReferralSteps.ReferTo,
          steps,
          submitText: 'Submit Referral',
          onCancel,
          onNextStep,
          onPreviousStep,
        }}
        resetOnInitialValuesChange>
        <ReferralForm
          locationEpisode={locationEpisode}
          initialPhoneNumber={latestReferralData?.phoneNumber ?? importedPatientValues?.phoneNumber}
        />
      </FormProvider>
    </StandardModal>
  );
}
